import { useGetEligiblePlans, useGetPlanCalculations } from './hooks';
import { PlansList, deduplicatePlans } from './PlansList';
import { InvalidLoanAmountState } from './InvalidLoanAmountState';
import { NoPlansAvailableState } from './NoPlansAvailableState';
import { PreApprovalSection } from './PreApprovalSection';
import { usePreApprovalDisplay } from './usePreApprovalDisplay';
import { GreenSkyPreApprovalDisclosure } from '../disclosures';
import { Calculation, ErrorInfo } from '@servicetitan/financing-api-client';
import { SetupDataStore, useProviderConfig } from '@servicetitan/financing-workflow-shared';
import {
    LoadingScreen,
    isRenderableBannerPreApproval,
    PlansListFirstContentGate,
    PlansListFirstContentProvider,
} from '@servicetitan/financing-utils-shared';
import { useLoanAmountValidation } from '../providers/useLoanAmountValidation';
import { FinancedAmountStore } from '../financing-information/financedAmountStore';
import { CustomPlansStore } from './CustomPlansStore';
import { PreApprovalStore } from './preApprovalStore';
import { useEffect, useMemo, useRef } from 'react';
import {
    buildAmountRangeContextKey,
    buildAmountRangeSearchKey,
    buildAmountUnavailableMessage,
    AmountRangeStatus,
    sortEligibleCalculations,
} from './amountRange';
import { AmountRangeStore } from '../financing-information/amountRangeStore';
import { UnavailableAmountState } from './UnavailableAmountState';
import { usePreApprovalEligibility } from './usePreApprovalEligibility';
import { projectPreApprovalDisplay } from './preApprovalDisplay';
import { AffirmPaymentOptionsScreen } from '../affirm-payment-options';
import { Alert } from '@servicetitan/anvil2';
import {
    AFFIRM_RANGE_NOTICE_TEXT,
    hasAffirmCalculation,
    shouldShowAffirmRangeNotice,
} from './affirmRangeNotice';

declare module '@tanstack/react-query' {
    interface Register {
        defaultError: ErrorInfo;
    }
}

const SELECTOR_TITLE_DEFAULT = 'Select a Plan';
const SELECTOR_TITLE_OVERRIDE = 'Not seeing what you want? Check eligibility for other plans';
const SELECTOR_TITLE_ALL_PREAPPROVED = "Select a plan. You're pre-approved for all of them.";
const EMPTY_CALCULATIONS: Calculation[] = [];

// A technician runs inside the FMA webview, where applying for Affirm (opening the Affirm-hosted
// prequalification URL) is not allowed. Whenever Affirm plans reach a select-plan flow there —
// including via a range gate that returns Affirm under another setup provider — the plan list is
// replaced by the informational AffirmPaymentOptionsScreen (sample payments + share instructions).
// Homeowners keep the plan list but get an info notice (see shouldShowAffirmRangeNotice) explaining
// why Affirm plans appear under the provider they requested.

interface ContainerProps {
    onPlanSelected?: (plan: Calculation) => void;
    amountRangeEnabled?: boolean;
}

const LegacyContainer = ({ onPlanSelected }: ContainerProps) => {
    const { financedAmount } = FinancedAmountStore.useStore();
    const { setupData, setSetupData } = SetupDataStore.useStore();
    const { preApproval } = PreApprovalStore.useStore();
    const {
        customPlanIds,
        hasCustomPlans,
        provider: providerFromUrl,
    } = CustomPlansStore.useStore();
    const { planSelectionInstructions, clearsPreApprovalOnRegularPlanSelect } = useProviderConfig();
    // When the currently selected provider is the same as the provider from the URL, use the custom plans
    const useCustomPlans = providerFromUrl === setupData?.provider && hasCustomPlans;

    const { data, isLoading, isError } = useGetEligiblePlans({
        providerName: setupData?.provider ?? '',
        tenantId: setupData?.tenantId ?? null,
        buId: setupData?.buId ?? null,
        amount: financedAmount!,
        allPlans: true,
        state: setupData?.state,
        useAdditionalPromotions: true,
        planList: useCustomPlans ? customPlanIds : null,
        // For FinanceIt, always request all payment frequencies to get comprehensive plan data
        paymentFrequency: setupData?.provider?.toLowerCase() === 'financeit' ? 'all' : null,
        merchantId: 'all',
    });

    const { noPlansEligible, minimumAmount } = data ?? {};
    const validation = useLoanAmountValidation(financedAmount);
    const display = usePreApprovalDisplay();

    const plans = data?.data ?? [];

    if (setupData?.role === 'technician' && hasAffirmCalculation(plans)) {
        return <AffirmPaymentOptionsScreen />;
    }

    // A failed/absent list query (the eligible-plans query is also disabled for an out-of-bounds
    // amount, reporting no data) renders blank, exactly as today.
    const listIsError = isError || (!isLoading && data == null);

    const isPreApprovalShown = display.kind === 'visible' || display.kind === 'outOfRange';

    // A banner (non-card) pre-approval covers every plan; card providers take the OVERRIDE title instead.
    const hasBannerPreApproval = isRenderableBannerPreApproval(setupData?.provider, preApproval);
    const selectorTitle = isPreApprovalShown
        ? SELECTOR_TITLE_OVERRIDE
        : hasBannerPreApproval
          ? SELECTOR_TITLE_ALL_PREAPPROVED
          : SELECTOR_TITLE_DEFAULT;

    // The page-level loading / invalid-amount / no-plans notices only apply with no pre-approval to
    // show: a visible/loading pre-approval resolves on its own clock and suppresses them so the card
    // can render even when the BU-rule list is empty. The order matters — validation must win over
    // listIsError, since an out-of-bounds amount disables the list query (no data) yet must still
    // show the "between …" notice rather than a blank page.
    if (display.kind === 'hidden') {
        if (isLoading) {
            return <LoadingScreen message="Loading available plans..." />;
        }
        if (!validation.isValidLoanAmount) {
            return (
                <InvalidLoanAmountState
                    minimumLoanAmount={validation.minAmount}
                    maximumLoanAmount={validation.maxAmount}
                />
            );
        }
        if (listIsError) {
            return null;
        }
        if (plans.length === 0 && noPlansEligible) {
            return <InvalidLoanAmountState minimumLoanAmount={minimumAmount!} />;
        }
        if (plans.length === 0) {
            return <NoPlansAvailableState />;
        }
    }

    // preApprovalId is the single source of truth for the hard credit application — useProviderConfig
    // and the submission/refresh hooks key the GreenSky hard/soft decision off it — so none of these
    // paths touch isApplyingForPrequalification. Leaving the soft flag alone is load-bearing: a
    // customer who started in the soft prequal flow, briefly picked the pre-approval, then picked a
    // regular plan returns to the soft app instead of being stranded in the hard config. All writes
    // use a functional merge so other setup fields (provider/tenantId/etc.) survive.
    const selectRegular = (plan: Calculation) => {
        if (hasBannerPreApproval) {
            // A banner pre-approval (Synchrony) has no plan card, so picking any plan applies with the
            // offer: anchor its id (the banner is the only source of it — no card, not URL-seeded like
            // the homeowner flow) and clear the prequal flag so it submits as a full application.
            // Activity is logged at the external application call (AcceptApply) and on the not-me link
            // (NotMeApply), never on plan select.
            setSetupData(prev => ({
                ...prev!,
                preApprovalId: preApproval?.id ?? undefined,
                isApplyingForPrequalification: false,
            }));
        } else if (clearsPreApprovalOnRegularPlanSelect) {
            // GreenSky surfaces the pre-approval as a plan card, so a regular pick opts out — clear the
            // anchor. Returning the previous reference when there's nothing to clear makes React bail
            // out, so non-pre-approval consumers (update-offer, authorize/change-order edit-plan) don't
            // re-render every SetupDataStore subscriber on a plain plan pick.
            setSetupData(prev =>
                prev?.preApprovalId ? { ...prev, preApprovalId: undefined } : prev,
            );
        }
        onPlanSelected?.(plan);
    };
    const preApprovalId = isPreApprovalShown ? (display.preApproval.id ?? undefined) : undefined;
    const selectPreApproval = (plan: Calculation) => {
        setSetupData(prev => ({ ...prev!, preApprovalId }));
        onPlanSelected?.(plan);
    };

    // Exclude the pre-approved plan from the selector so it isn't rendered twice (and so picking it
    // there can't route it to the soft app via selectRegular). This applies for every non-hidden
    // state — including `loading`, before the card resolves: otherwise a quick pick of that plan
    // during the calc would clear preApprovalId and start the soft prequal instead of the hard credit
    // application, and the plan would briefly list in both the selector and the resolving card.
    // Pre-approval is GreenSky-only, where merchant/frequency tabs are off, so the deduplicated set
    // equals the selector's displayed set — hasOtherPlans here matches what PlansList renders.
    const preApprovedPlanNumber =
        display.kind === 'hidden' ? undefined : display.preApproval.planNumber;
    const selectablePlans =
        preApprovedPlanNumber != null
            ? deduplicatePlans(plans).filter(p => p.planNumber !== preApprovedPlanNumber)
            : plans;
    const hasOtherPlans = selectablePlans.length > 0;

    const affirmNotice = shouldShowAffirmRangeNotice({
        requestedProvider: setupData?.provider,
        role: setupData?.role,
        plans: selectablePlans,
    }) ? (
        <Alert status="info" title={AFFIRM_RANGE_NOTICE_TEXT} />
    ) : undefined;

    // The card area is a placeholder while the targeted calc is in flight, the pre-approval section
    // once it resolves, and nothing otherwise. It is the selector's leading content so both share
    // the `.plans-container` container-query context.
    const leadingContent = isPreApprovalShown ? (
        <PreApprovalSection
            display={display}
            hasOtherPlans={hasOtherPlans}
            onPlanSelected={selectPreApproval}
        />
    ) : display.kind === 'loading' ? (
        <LoadingScreen message="Checking your pre-approval..." />
    ) : undefined;

    return (
        <PlansListFirstContentGate>
            <PlansList
                plans={selectablePlans}
                instructions={planSelectionInstructions}
                onPlanSelected={selectRegular}
                title={selectorTitle}
                // On visible/outOfRange with no other plans, the selector (and its empty/no-plans notice)
                // is suppressed entirely — the pre-approval section is the page body.
                showSelector={!(isPreApprovalShown && !hasOtherPlans)}
                leadingContent={leadingContent}
                notice={affirmNotice}
                leadingDisclosure={
                    isPreApprovalShown ? (
                        <GreenSkyPreApprovalDisclosure text={display.generalDisclosure} />
                    ) : undefined
                }
            />
        </PlansListFirstContentGate>
    );
};

interface AmountRangeHistory {
    contextKey: string;
    plans: Calculation[];
    hasEverBeenInRange: boolean;
}

const AmountRangeContainer = ({ onPlanSelected }: ContainerProps) => {
    const { financedAmount } = FinancedAmountStore.useStore();
    const { setupData, setSetupData } = SetupDataStore.useStore();
    const { preApproval } = PreApprovalStore.useStore();
    const {
        customPlanIds,
        hasCustomPlans,
        provider: providerFromUrl,
    } = CustomPlansStore.useStore();
    const { planSelectionInstructions, clearsPreApprovalOnRegularPlanSelect } = useProviderConfig();
    const { setAmountRange } = AmountRangeStore.useStore();
    const validation = useLoanAmountValidation(financedAmount);
    const preApprovalEligibility = usePreApprovalEligibility();
    const historyRef = useRef<AmountRangeHistory | null>(null);
    const useCustomPlans = providerFromUrl === setupData?.provider && hasCustomPlans;
    const planList = useCustomPlans ? customPlanIds : null;
    const params = {
        providerName: setupData?.provider ?? '',
        tenantId: setupData?.tenantId ?? null,
        buId: setupData?.buId ?? null,
        amount: financedAmount!,
        allPlans: true,
        state: setupData?.state,
        useAdditionalPromotions: true,
        planList,
        paymentFrequency: setupData?.provider?.toLowerCase() === 'financeit' ? 'all' : null,
        merchantId: 'all',
    };
    const query = useGetPlanCalculations(params);
    const calculations = query.data?.data ?? EMPTY_CALCULATIONS;
    const eligiblePlans = sortEligibleCalculations(calculations);
    const contextInput = {
        provider: setupData?.provider,
        tenantId: setupData?.tenantId,
        customerId: setupData?.customerId,
        buId: setupData?.buId,
        state: setupData?.state,
        customPlanIds: planList,
    };
    const contextKey = buildAmountRangeContextKey(contextInput);
    const searchKey = buildAmountRangeSearchKey({ ...contextInput, financedAmount });
    const history = historyRef.current?.contextKey === contextKey ? historyRef.current : undefined;
    const hasCurrentInRangeOption =
        eligiblePlans.length > 0 || preApprovalEligibility.kind === 'in-range';
    const isLoading =
        query.isLoading ||
        (!history?.hasEverBeenInRange &&
            eligiblePlans.length === 0 &&
            preApprovalEligibility.kind === 'loading');
    const isUnavailable = !isLoading && !query.isError && !hasCurrentInRangeOption;
    const status: AmountRangeStatus = isLoading
        ? 'loading'
        : hasCurrentInRangeOption
          ? 'in-range'
          : history?.hasEverBeenInRange && isUnavailable
            ? 'out-of-range'
            : 'never-in-range';
    const message = useMemo(
        () =>
            buildAmountUnavailableMessage({
                financedAmount,
                minAmount: validation.minAmount,
                maxAmount: validation.maxAmount,
                calculations: query.isSuccess ? calculations : undefined,
            }),
        [calculations, financedAmount, query.isSuccess, validation.maxAmount, validation.minAmount],
    );

    useEffect(() => {
        if (historyRef.current?.contextKey !== contextKey) {
            historyRef.current = { contextKey, plans: [], hasEverBeenInRange: false };
        }
    }, [contextKey]);

    useEffect(() => {
        if (!hasCurrentInRangeOption) {
            return;
        }

        const currentHistory =
            historyRef.current?.contextKey === contextKey
                ? historyRef.current
                : { contextKey, plans: [], hasEverBeenInRange: false };
        historyRef.current = {
            contextKey,
            plans: eligiblePlans.length > 0 ? eligiblePlans : currentHistory.plans,
            hasEverBeenInRange: true,
        };
    }, [contextKey, eligiblePlans, hasCurrentInRangeOption]);

    useEffect(() => {
        setAmountRange({
            status: query.isError ? null : status,
            message:
                query.isError || status === 'loading' || status === 'in-range' ? null : message,
            searchKey,
        });
    }, [message, query.isError, searchKey, setAmountRange, status]);

    if (query.isError) {
        return null;
    }

    if (isLoading) {
        return <LoadingScreen message="Loading available plans..." />;
    }

    // Plans actually shown: out-of-range falls back to the last in-range set (history). The
    // technician Affirm gate must inspect these too, not just the live `calculations` — otherwise a
    // technician whose amount moved out of range after an Affirm swap would see the historical Affirm
    // plans in the list (Affirm has dropped from `calculations` by then) instead of the gate.
    const plans = status === 'out-of-range' ? (history?.plans ?? []) : eligiblePlans;

    if (
        setupData?.role === 'technician' &&
        (hasAffirmCalculation(calculations) || hasAffirmCalculation(plans))
    ) {
        return <AffirmPaymentOptionsScreen />;
    }

    if (status === 'never-in-range') {
        return <UnavailableAmountState message={message} />;
    }

    const display = projectPreApprovalDisplay(status, preApprovalEligibility);
    const isPreApprovalShown =
        display.kind === 'visible' ||
        display.kind === 'outOfRange' ||
        display.kind === 'unavailable';
    const hasBannerPreApproval = isRenderableBannerPreApproval(setupData?.provider, preApproval);
    const selectorTitle = isPreApprovalShown
        ? SELECTOR_TITLE_OVERRIDE
        : hasBannerPreApproval
          ? SELECTOR_TITLE_ALL_PREAPPROVED
          : SELECTOR_TITLE_DEFAULT;
    const preApprovedPlanNumber =
        display.kind === 'hidden' ? undefined : display.preApproval?.planNumber;
    const selectablePlans =
        preApprovedPlanNumber == null
            ? plans
            : deduplicatePlans(plans).filter(plan => plan.planNumber !== preApprovedPlanNumber);
    const hasOtherPlans = selectablePlans.length > 0;

    // Only when the plans are currently available (in-range). On out-of-range the cards come from
    // history and render as `unavailable`, so the "qualifies / plans available" copy would contradict
    // the disabled cards after the homeowner edits the amount out of Affirm's range.
    const affirmNotice =
        status === 'in-range' &&
        shouldShowAffirmRangeNotice({
            requestedProvider: setupData?.provider,
            role: setupData?.role,
            plans: selectablePlans,
        }) ? (
            <Alert status="info" title={AFFIRM_RANGE_NOTICE_TEXT} />
        ) : undefined;

    const selectRegular = (plan: Calculation) => {
        if (hasBannerPreApproval) {
            setSetupData(previous => ({
                ...previous!,
                preApprovalId: preApproval?.id ?? undefined,
                isApplyingForPrequalification: false,
            }));
        } else if (clearsPreApprovalOnRegularPlanSelect) {
            setSetupData(previous =>
                previous?.preApprovalId ? { ...previous, preApprovalId: undefined } : previous,
            );
        }
        onPlanSelected?.(plan);
    };
    const selectPreApproval = (plan: Calculation) => {
        const preApprovalId =
            display.kind === 'hidden' || display.kind === 'loading'
                ? undefined
                : (display.preApproval.id ?? undefined);
        setSetupData(previous => ({ ...previous!, preApprovalId }));
        onPlanSelected?.(plan);
    };
    const leadingContent = isPreApprovalShown ? (
        <PreApprovalSection
            display={display}
            hasOtherPlans={hasOtherPlans}
            onPlanSelected={selectPreApproval}
        />
    ) : display.kind === 'loading' ? (
        <LoadingScreen message="Checking your pre-approval..." />
    ) : undefined;

    if (status === 'out-of-range' && plans.length === 0 && !isPreApprovalShown) {
        return <UnavailableAmountState message={message} />;
    }

    return (
        <PlansListFirstContentGate>
            <PlansList
                plans={selectablePlans}
                instructions={planSelectionInstructions}
                onPlanSelected={selectRegular}
                title={selectorTitle}
                showSelector={!(isPreApprovalShown && !hasOtherPlans)}
                leadingContent={leadingContent}
                notice={affirmNotice}
                leadingDisclosure={
                    isPreApprovalShown ? (
                        <GreenSkyPreApprovalDisclosure text={display.generalDisclosure} />
                    ) : undefined
                }
                unavailable={status === 'out-of-range'}
            />
        </PlansListFirstContentGate>
    );
};

export const Container = ({ amountRangeEnabled = false, ...props }: ContainerProps) => {
    const { setupData } = SetupDataStore.useStore();

    return (
        <PlansListFirstContentProvider
            context={{ provider: setupData?.provider, experience: setupData?.role }}
        >
            {amountRangeEnabled ? (
                <AmountRangeContainer {...props} />
            ) : (
                <LegacyContainer {...props} />
            )}
        </PlansListFirstContentProvider>
    );
};
